/**
 * ============================================================================
 *  sequenceTitre.ts — Le minutage du titre d'arrivée, sans React ni horloge
 * ----------------------------------------------------------------------------
 *  Référence : handoff `design_handoff_titre_scene`, variante « signature ».
 *  Cinq secondes d'introduction, à partir de l'instant où la scène est PRÊTE
 *  (texture affichée), puis le titre RESTE, réduit, tant que la scène dure :
 *
 *    0       attente   rien encore : la scène finit d'apparaître
 *    +0,25 s affiche   entrée (fondu et glissement, portés par le CSS)
 *    +4,1 s  sortie    le titre rapetisse vers sa forme réduite (0,9 s)
 *    +5 s    compact   forme réduite, définitive jusqu'à la scène suivante
 *
 *  `masque` ne vaut plus que sans séquence (rien n'est arrivé encore).
 *
 *  L'introduction a une durée FIXE : un mouvement de caméra ne l'écourte pas. Seule
 *  l'ouverture de la navigation sur téléphone lance la sortie tout de suite
 *  (`interrompre`) ; une nouvelle scène prête relance tout depuis 0.
 *
 *  Les instants sont DONNÉS, jamais lus : le composant passe `performance.now()`,
 *  les tests des nombres. C'est ce qui rend la séquence vérifiable sans minuteur.
 * ==========================================================================*/

export const ENTREE_MS = 250;
export const SORTIE_MS = 4100;
export const FIN_MS = 5000;
/** Durée de la sortie, interrompue ou non. */
export const DUREE_SORTIE_MS = FIN_MS - SORTIE_MS;

export type Phase = "masque" | "attente" | "affiche" | "sortie" | "compact";

export interface Sequence {
  sceneId: string;
  /** Instant où la scène est prête. */
  debut: number;
  /** Instant d'une sortie anticipée (navigation ouverte sur téléphone) ; `null` : sortie à l'heure. */
  sortieAnticipee: number | null;
}

/** Une scène est prête : la séquence repart de zéro, quelle que soit la précédente. */
export function demarrer(sceneId: string, instant: number): Sequence {
  return { sceneId, debut: instant, sortieAnticipee: null };
}

/** Instant où la sortie commence. */
function debutSortie(sequence: Sequence): number {
  return Math.min(sequence.sortieAnticipee ?? Infinity, sequence.debut + SORTIE_MS);
}

export function phaseA(sequence: Sequence | null, instant: number): Phase {
  if (!sequence) return "masque";
  const sortie = debutSortie(sequence);
  if (instant >= sortie + DUREE_SORTIE_MS || instant >= sequence.debut + FIN_MS) return "compact";
  if (instant >= sortie) return "sortie";
  return instant < sequence.debut + ENTREE_MS ? "attente" : "affiche";
}

/**
 * Lancer la sortie maintenant. Sans effet si elle a déjà commencé, ou si le titre
 * est déjà réduit. Pendant l'attente (le titre n'est pas encore entré), il n'y a rien
 * à faire sortir : la séquence s'arrête net.
 */
export function interrompre(sequence: Sequence | null, instant: number): Sequence | null {
  const phase = phaseA(sequence, instant);
  if (!sequence || phase === "masque" || phase === "sortie" || phase === "compact") return sequence;
  if (phase === "attente") return null;
  return { ...sequence, sortieAnticipee: instant };
}

/** Prochain changement de phase après `instant`, pour poser un seul minuteur ; `null` : plus rien. */
export function prochainChangement(sequence: Sequence | null, instant: number): number | null {
  if (!sequence) return null;
  const sortie = debutSortie(sequence);
  const fin = Math.min(sortie + DUREE_SORTIE_MS, sequence.debut + FIN_MS);
  const jalons = [sequence.debut + ENTREE_MS, sortie, fin];
  return jalons.find((jalon) => jalon > instant) ?? null;
}
